<template>
    <div class="room-plan-view" :class="{ 'transfer-bill-mode': isTransferBillMode }">
        <section v-if="isTransferBillMode" class="transfer-bill-actions">
            <strong>
                {{ translations.roomPlan.selectTableToTransferBill }}
            </strong>
            <VButton class="cancel-bill-transfer-btn warning rounded" @click="cancelTransferBill">
                {{ translations.general.cancel }}
            </VButton>
        </section>

        <ZoomableContainer v-if="!isLoading && room != null" class="room-container" :defaultScale="defaultScale">
            <div class="room" :class="[roomShapeClass]">
                <RoomPlanTable
                    v-for="table of tablesToDisplay"
                    :key="table.id"
                    :table="table"
                    :disabled="isTableDisabled(table)"
                    @tableSelected="onTableSelected"
                />
            </div>
        </ZoomableContainer>

        <div v-if="isLoading" class="loading-container">
            <div class="loading">
                <div class="loader green"></div>
                {{ translations.roomPlan.loadingTables }}
            </div>
        </div>

        <section class="footer">
            <div v-if="currentRoomName" class="current">
                {{ currentRoomName }}

                <ChangeRoomList
                    :locationId="location!.id"
                    :currentRoomId="room!.id"
                    overlayTarget=".view.room-plan"
                    @roomSelected="onRoomSelected"
                />
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { RoomDao } from "@/data/dao/service/RoomDao";
    import { dataEvents } from "@/data/events/dataEvents";
    import { RoomShape } from "@/data/model/Enums";
    import type {
        IBillClosedEvent,
        IBillManualEndUsersCountChangedEvent,
        IRoomPlanChangedEvent,
        ITableCovidUserCountChangedEvent,
    } from "@/data/model/Events";
    import type { ILocationBasicInfo } from "@/data/model/Location";
    import type { IRoomPlanTable } from "@/data/model/Operator";
    import type { IRoomWithService } from "@/data/model/Room";
    import type { TransferBillOptions } from "@/data/model/Table";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";
    import ZoomableContainer from "@/components/layout/ZoomableContainer.vue";
    import ChangeRoomList from "@/components/roomPlan/ChangeRoomList.vue";
    import RoomPlanTable from "@/components/roomPlan/RoomPlanTable.vue";
    import { getLocationName } from "@/shared/helpers/LocationHelper";

    // define properties
    const props = defineProps<{
        transferBillOptions?: TransferBillOptions;
        scaleLandscapeView?: boolean;
    }>();

    const emit = defineEmits(["tableSelected"]);

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    const roomDao = Factory.getInstance<RoomDao>(RoomDao);

    const changeRoomListComponent = ref<InstanceType<typeof ChangeRoomList> | null>(null);

    const location = ref<ILocationBasicInfo | undefined>(undefined);
    const room = ref<IRoomWithService | undefined>(undefined);
    const tables = ref<IRoomPlanTable[]>([]);

    const tablesToDisplay = computed(() => tables.value.filter((t) => t.dimension != null));
    const currentRoomName = computed(() =>
        location.value != null && room.value != null ? `${getLocationName(location.value)} - ${room.value.name}` : null
    );
    const roomShapeClass = computed(() => {
        if (room.value?.shape == RoomShape.Landscape) return "landscape";
        if (room.value?.shape == RoomShape.Portrait) return "portrait";
        return "square";
    });
    const isLoading = ref<boolean>(false);
    const defaultScale = computed(() => {
        if (props.scaleLandscapeView && room.value?.shape == RoomShape.Landscape) {
            return 1;
            // return 1.5;
        }
        return 1;
    });

    function isTableDisabled(table: IRoomPlanTable) {
        return isTransferBillMode.value && table.id == transferBillOperationOptions.value?.currentTableId;
    }

    function reset() {
        location.value = undefined;
        room.value = undefined;
        tables.value = [];
    }

    async function loadRoomPlan(options: {
        location: ILocationBasicInfo;
        roomId?: string;
        room?: IRoomWithService;
    }): Promise<void> {
        if (options.roomId === undefined && options.room === undefined) {
            logError("RoomPlanViewComponent.vue loadRoomPlan: roomId and roomData are undefined");
            return;
        }

        isLoading.value = true;
        reset();

        try {
            location.value = options.location;
            room.value = options.room ?? (await roomDao.getSingle(options.roomId!));

            tables.value = await operatorDao.getRoomPlan({ roomId: room.value!.id });
        } catch (ex) {
            logError(ex);
            reset();

            Popup.show({
                title: translations.general.error,
                message: translations.roomPlan.failedLoadingTables,
            });
        } finally {
            isLoading.value = false;
            await nextTick();
        }
    }

    function onRoomSelected(room: IRoomWithService) {
        loadRoomPlan({
            location: location.value!,
            room: room,
        });
    }

    function onTableSelected(table: IRoomPlanTable) {
        if (isTransferBillMode.value) {
            selectTableToTransferBill(table);
            return;
        }

        emit("tableSelected", table);
    }

    function onGuestNumberChanged(ev: ITableCovidUserCountChangedEvent) {
        //get table and update guests number
        const table = tables.value.find((t) => t.id == ev.tableId);

        if (table) {
            table.allocations = ev.covidUsersAtTable;
        }
    }

    function onManualEndUsersCountChanged(ev: IBillManualEndUsersCountChangedEvent) {
        //get table and update manual guests number
        const table = tables.value.find((t) => t.id == ev.tableId);

        if (table) {
            table.manualEndUsersCount = ev.manualEndUsersCount;
        }
    }

    function onBillClosed(ev: IBillClosedEvent) {
        //get table and update manual guests number
        const table = tables.value.find((t) => t.id == ev.tableId);

        if (table) {
            table.manualEndUsersCount = undefined;
        }
    }

    function onRoomPlanChanged(ev: IRoomPlanChangedEvent) {
        // ignore if changed room is not the same as currently displayed
        if (ev.roomId && room.value?.id !== ev.roomId) {
            return;
        }

        loadRoomPlan({ location: location.value!, roomId: ev.roomId });
    }

    /* Transfer bill actions */
    const transferBillOperationOptions = ref(props.transferBillOptions);
    const isTransferBillMode = computed(() => transferBillOperationOptions.value != null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function selectTableToTransferBill(table: IRoomPlanTable) {
        transferBillOperationOptions.value = undefined;
        // todo: accept transfer in backend
        isLoading.value = true;
        setTimeout(() => {
            isLoading.value = false;
        }, 1000);
    }

    function cancelTransferBill() {
        transferBillOperationOptions.value = undefined;
        // todo: cancel transfer in backend
    }

    function getCurrentRoom() {
        return room.value;
    }

    onMounted(async () => {
        dataEvents.operatorSocket.TableCovidUserCountChanged.add(onGuestNumberChanged);
        dataEvents.operatorSocket.BillManualEndUsersCountChanged.add(onManualEndUsersCountChanged);
        dataEvents.operatorSocket.BillClosed.add(onBillClosed);
        dataEvents.operatorSocket.RoomPlanChanged.add(onRoomPlanChanged);

        dataEvents.appSocket.TableCovidUserCountChanged.add(onGuestNumberChanged);
        dataEvents.appSocket.BillManualEndUsersCountChanged.add(onManualEndUsersCountChanged);
        dataEvents.appSocket.BillClosed.add(onBillClosed);
        dataEvents.appSocket.RoomPlanChanged.add(onRoomPlanChanged);
    });

    onBeforeUnmount(() => {
        dataEvents.operatorSocket.TableCovidUserCountChanged.remove(onGuestNumberChanged);
        dataEvents.operatorSocket.BillManualEndUsersCountChanged.remove(onManualEndUsersCountChanged);
        dataEvents.operatorSocket.BillClosed.remove(onBillClosed);
        dataEvents.operatorSocket.RoomPlanChanged.remove(onRoomPlanChanged);

        dataEvents.appSocket.TableCovidUserCountChanged.remove(onGuestNumberChanged);
        dataEvents.appSocket.BillManualEndUsersCountChanged.remove(onManualEndUsersCountChanged);
        dataEvents.appSocket.BillClosed.remove(onBillClosed);
        dataEvents.appSocket.RoomPlanChanged.remove(onRoomPlanChanged);

        changeRoomListComponent.value?.close();
    });

    defineExpose({
        loadRoomPlan,
        getCurrentRoom,

        isTransferBillMode,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .room-plan-view {
        display: flex;
        flex-direction: column;

        .transfer-bill-actions {
            padding: 0.5em 16px;
            text-align: right;

            strong {
                color: colors.$fontColor;
                font-size: 16px;
                margin-right: 0.5em;
            }
        }

        .room-container {
            flex: 1 1 auto;
            position: relative;
            height: auto;

            .room {
                box-sizing: border-box;
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                margin: auto;
                max-width: 100%;
                max-height: 100%;
                aspect-ratio: 1 / 1;

                &.landscape {
                    aspect-ratio: 10 / 7;
                }

                &.portrait {
                    aspect-ratio: 10 / 13;
                }
            }
        }

        .loading-container {
            position: relative;
            flex: 1 1 auto;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .footer {
            box-sizing: border-box;
            border-top: 1px solid #ccc;
            padding: 5px 0;

            > .current {
                color: colors.$fontColor;
                padding: 0 16px;
                height: 40px;
                line-height: 40px;
                font-weight: 600;
                font-size: 14px;
                text-align: right;
            }
        }
    }
</style>
